import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { formatMoney } from '../utils/format';
import { isDemo, demoJackpot } from '../dev/demo';

// "You won the jackpot!" — shown once per device to each winner, after the monthly draw.
// Everyone else just sees the winners on the jackpot card, unless a winner was replaced: that new draw is shown once to everyone.
const KEY = 'jackpotWinSeen';
const REDRAW_KEY = 'jackpotRedrawSeen';
const MONTHS = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  fr: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'],
};

// the prize counts up to its real amount
function CountUp({ value, lang, delay = 450, duration = 1400 }) {
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(value); return undefined; }
    let frame;
    const start = performance.now() + delay;
    const tick = (now) => {
      const p = Math.min(1, Math.max(0, (now - start) / duration));
      setShown(p === 1 ? value : value * (1 - Math.pow(1 - p, 4)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, delay, duration]);
  return <>+{formatMoney(shown, lang)}</>;
}

// a winner who left: their name is crossed out, then the names spin, slow down and land on the new winner
const REDRAW_DELAYS = Array.from({ length: 26 }, (_, i) => 55 + Math.pow(i / 25, 3) * 480);
function Redraw({ from, to, names, place, t, onDone }) {
  const [phase, setPhase] = useState('cross');   // cross | spin | landed
  const [name, setName] = useState(from);
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { onDone(); return undefined; }
    const pool = [...names.filter((n) => n !== from && n !== to), to];   // the new winner spins with the others
    const list = pool;
    let timer;
    let last = null;
    const spin = (i) => {
      if (i === REDRAW_DELAYS.length) {
        setName(to);
        setPhase('landed');
        timer = setTimeout(onDone, 1300);
        return;
      }
      const options = list.filter((n) => n !== last && (i < REDRAW_DELAYS.length - 1 || n !== to));
      last = options.length ? options[Math.floor(Math.random() * options.length)] : to;
      setName(last);
      timer = setTimeout(() => spin(i + 1), REDRAW_DELAYS[i]);
    };
    timer = setTimeout(() => { setPhase('spin'); spin(0); }, 2000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line
  return (
    <div className={`jw-prize jw-redraw is-${phase}`}>
      <span className="jw-place">{t.place(place)}</span>
      <b className="jw-redraw-name" key={phase === 'spin' ? 'spin' : phase}>{name}</b>
      <span className="jw-sub">{phase === 'cross' ? t.left : phase === 'spin' ? t.picking : t.picked}</span>
    </div>
  );
}

export default function JackpotWin({ lang = 'en', profile, blocked }) {
  const [draw, setDraw] = useState(null);
  const [open, setOpen] = useState(false);
  const [redraw, setRedraw] = useState(false);
  const [names, setNames] = useState([]);
  const t = lang === 'fr' ? TR.fr : TR.en;
  const confetti = useMemo(() => Array.from({ length: 36 }, (_, i) => ({
    '--x': `${(i * 37) % 100}%`,
    '--drift': `${((i * 53) % 120) - 60}px`,
    '--spin': `${360 + (i * 97) % 720}deg`,
    '--delay': `${0.25 + ((i * 29) % 90) / 100}s`,
    '--fall': `${1.9 + ((i * 17) % 13) / 10}s`,
    '--c': ['var(--ui-accent, var(--accent))', '#e0a526', 'var(--win)', '#c8102e'][i % 4],
  })), []);

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    (async () => {
      let last = demoJackpot(profile)?.last || null;
      if (!last) {
        const { data } = await supabase.from('jackpots').select('month, pot, winners').not('drawn_at', 'is', null).order('month', { ascending: false }).limit(1);
        last = data?.[0] || null;
      }
      if (!alive || !last?.winners?.length) return;
      const mine = last.winners.find((w) => w.user_id === profile.id);
      let seen = null;
      try { seen = localStorage.getItem(KEY); } catch (e) { /* private mode */ }
      const redrawn = last.winners.find((w) => w.replaced);
      let redrawSeen = null;
      try { redrawSeen = localStorage.getItem(REDRAW_KEY); } catch (e) { /* private mode */ }
      const showRedraw = redrawn && (isDemo() || redrawSeen !== last.month);
      if (showRedraw || (mine && (isDemo() || seen !== last.month))) {
        if (showRedraw) {
          // the pick spins through that month's ticket holders
          const { data: tickets } = await supabase.from('jackpot_tickets').select('user_id').eq('month', last.month);
          const ids = [...new Set((tickets || []).map((x) => x.user_id))];
          const { data: players } = ids.length ? await supabase.from('profiles').select('username').in('id', ids) : { data: [] };
          if (!alive) return;
          setNames([...new Set((players || []).map((p) => p.username))].filter((n) => n && !last.winners.some((w) => w.username === n)));   // those who already won aren't in the pick
          setRedraw(true);
        }
        setDraw({ ...last, mine, redrawn });
        setOpen(true);
      }
    })();
    return () => { alive = false; };
  }, [profile?.id]);

  if (!open || !draw || blocked) return null;
  const close = () => {
    if (!isDemo()) { try { localStorage.setItem(KEY, draw.month); localStorage.setItem(REDRAW_KEY, draw.month); } catch (e) { /* private mode */ } }
    setOpen(false);
  };
  const [y, m] = draw.month.split('-').map(Number);
  const monthName = `${MONTHS[lang === 'fr' ? 'fr' : 'en'][m - 1]} ${y}`;
  const byPlace = (n) => draw.winners.find((w) => w.place === n);
  const shown = draw.mine || byPlace(1);   // my prize, or the jackpot's when I didn't win
  if (redraw) {
    return (
      <div className="wn-backdrop">
        <div className="wn-modal jw-modal jw-win" role="dialog" aria-modal="true" aria-labelledby="jw-title">
          <div className="wn-head">
            <span className="wn-kicker">{t.kicker} · {monthName}</span>
            <h2 id="jw-title" className="wn-title">{t.redraw}</h2>
          </div>
          <Redraw from={draw.redrawn.replaced} to={draw.redrawn.username} names={names} place={draw.redrawn.place} t={t} onDone={() => setRedraw(false)} />
          <button type="button" className="wn-go jw-skip" onClick={() => setRedraw(false)}>{t.skip}</button>
        </div>
      </div>
    );
  }
  return (
    <div className="wn-backdrop">
      <div className="jw-confetti" aria-hidden="true">{confetti.map((c, i) => <i key={i} style={c} />)}</div>
      <div className="wn-modal jw-modal jw-win" role="dialog" aria-modal="true" aria-labelledby="jw-title">
        <div className="wn-head">
          <span className="wn-kicker">{t.kicker} · {monthName}</span>
          <h2 id="jw-title" className="wn-title">{draw.mine?.place === 1 ? t.title : <><span className="jw-name">{byPlace(1)?.username}</span> {t.titleOther}</>}</h2>
        </div>
        <div className="jw-prize">
          <span className="jw-place">{t.place(shown.place)}</span>
          <b className="jw-amount"><CountUp value={Number(shown.amount)} lang={lang} /></b>
          <span className="jw-sub">{draw.mine ? t.added : t.addedOther}</span>
        </div>
        <div className="wn-podium-wrap">
          <span className="wn-podium-title">{t.winners} · {t.pot} {formatMoney(draw.pot, lang)}</span>
          <div className="wn-podium">
            {[2, 1, 3].map((n) => {
              const w = byPlace(n);
              if (!w) return <div key={n} />;
              return (
                <div key={n} className={`wn-step place-${n}${w.user_id === profile?.id ? ' jw-me' : ''}`}>
                  <span className="jw-rank">{n}</span>
                  <span className="wn-step-name">{w.username}</span>
                  <span className="wn-step-block">
                    <b>{formatMoney(w.amount, lang)}</b>
                    <small>{n === 1 ? '60%' : n === 2 ? '30%' : '10%'}</small>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <button type="button" className="wn-go" onClick={close}>{t.go}</button>
      </div>
    </div>
  );
}

const TR = {
  en: {
    kicker: 'Monthly jackpot', title: 'You won the jackpot!', titleOther: 'won the jackpot!', addedOther: 'Already added to their balance.', winners: 'The draw', pot: 'pot',
    place: (n) => (n === 1 ? '1st place' : n === 2 ? '2nd place' : '3rd place'),
    added: 'Already added to your balance.', go: 'Nice!',
    redraw: 'New draw', left: 'No longer playing', picking: 'Picking a new winner...', picked: 'New winner!', skip: 'Skip',
  },
  fr: {
    kicker: 'Gros lot du mois', title: 'Tu as gagné le gros lot!', titleOther: 'gagne le gros lot!', addedOther: 'Déjà ajouté à son solde.', winners: 'Le tirage', pot: 'pot',
    place: (n) => (n === 1 ? '1re place' : n === 2 ? '2e place' : '3e place'),
    added: 'Déjà ajouté à ton solde.', go: 'Yes!',
    redraw: 'Nouveau tirage', left: 'Ne joue plus', picking: 'On pige un nouveau gagnant...', picked: 'Nouveau gagnant!', skip: 'Passer',
  },
};
